// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Contact from "./contact.js"
import ContactRelationshipType from "./contact-relationship-type.js"

const modelClassData = {
  "attributes": {
    "child_id": {
      "column": {
        "default": null,
        "name": "child_id",
        "null": false,
        "type": null
      },
      "name": "child_id",
      "selected_by_default": null,
      "translated": null
    },
    "contact_relationship_type_id": {
      "column": {
        "default": null,
        "name": "contact_relationship_type_id",
        "null": false,
        "type": null
      },
      "name": "contact_relationship_type_id",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "parent_id": {
      "column": {
        "default": null,
        "name": "parent_id",
        "null": false,
        "type": null
      },
      "name": "parent_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "relationship_type": {
      "column": null,
      "name": "relationship_type",
      "selected_by_default": false,
      "translated": null
    }
  },
  "className": "ContactRelationship",
  "collectionKey": "contact_relationships",
  "collectionName": "contact_relationships",
  "i18nKey": "contact_relationship",
  "camelizedLower": "contactRelationship",
  "name": "ContactRelationship",
  "nameDasherized": "contact-relationship",
  "pluralName": "contact_relationships",
  "ransackable_associations": [
    {
      "className": "ContactRelationship::Audit",
      "collectionName": "contact_relationship_audits",
      "foreignKey": "contact_relationship_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "ContactRelationshipAudit",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "child_id",
      "name": "child",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "ContactRelationshipType",
      "collectionName": "contact_relationship_types",
      "foreignKey": "contact_relationship_type_id",
      "name": "contact_relationship_type",
      "macro": "belongs_to",
      "resource_name": "ContactRelationshipType",
      "through": null
    },
    {
      "className": "ContactRelationship::Audit",
      "collectionName": "contact_relationship_audits",
      "foreignKey": "contact_relationship_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "ContactRelationshipAudit",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "parent_id",
      "name": "parent",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "child_id",
      "column": {
        "default": null,
        "name": "child_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "contact_relationship_type_id",
      "column": {
        "default": null,
        "name": "contact_relationship_type_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "parent_id",
      "column": {
        "default": null,
        "name": "parent_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "where_relationship_type"
    }
  ],
  "relationships": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "child_id",
      "name": "child",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "ContactRelationshipType",
      "collectionName": "contact_relationship_types",
      "foreignKey": "contact_relationship_type_id",
      "name": "contact_relationship_type",
      "macro": "belongs_to",
      "resource_name": "ContactRelationshipType",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "parent_id",
      "name": "parent",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    }
  ],
  "paramKey": "contact_relationship",
  "primaryKey": "id"
}

/** Frontend model for ContactRelationship. */
export default class ContactRelationship extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  childId() {
    return this.readAttributeUnderscore("child_id")
  }

  /** @returns {boolean} */
  hasChildId() {
    const value = this.childId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  contactRelationshipTypeId() {
    return this.readAttributeUnderscore("contact_relationship_type_id")
  }

  /** @returns {boolean} */
  hasContactRelationshipTypeId() {
    const value = this.contactRelationshipTypeId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  parentId() {
    return this.readAttributeUnderscore("parent_id")
  }

  /** @returns {boolean} */
  hasParentId() {
    const value = this.parentId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  relationshipType() {
    return this.readAttributeUnderscore("relationship_type")
  }

  /** @returns {boolean} */
  hasRelationshipType() {
    const value = this.relationshipType()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static schoolLeaderCreate(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "school_leader_create",
        collectionName: "contact_relationships",
        type: "collection"
      },
      commandArgs
    ))
  }

  /** @returns {import("./contact.js").default | null} */
  child() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "child"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadChild() {
    if (!("childId" in this)) throw new Error("Foreign key method wasn't defined: childId")

    const id = this.childId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "child",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }

  /** @returns {import("./contact-relationship-type.js").default | null} */
  contactRelationshipType() {
    return this._readBelongsToReflection({modelClass: ContactRelationshipType, reflectionName: "contact_relationship_type"})
  }

  /** @returns {Promise<import("./contact-relationship-type.js").default | null>} */
  loadContactRelationshipType() {
    if (!("contactRelationshipTypeId" in this)) throw new Error("Foreign key method wasn't defined: contactRelationshipTypeId")

    const id = this.contactRelationshipTypeId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "contact_relationship_type",
        model: this,
        modelClass: ContactRelationshipType
      },
      {ransack}
    )
  }

  /** @returns {import("./contact.js").default | null} */
  parent() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "parent"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadParent() {
    if (!("parentId" in this)) throw new Error("Foreign key method wasn't defined: parentId")

    const id = this.parentId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "parent",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }
}
